import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { classic42 } from '@risk/maps';
import type { GameState, SeatId, TerritoryId } from '@risk/engine';

/**
 * The board.
 *
 * Pan and zoom move a wrapping `<g transform>` rather than the viewBox: that
 * composites on the GPU instead of forcing a layout pass every frame, which is
 * the difference between smooth and unusable on a mid-range phone.
 *
 * Three things are counter-scaled so they keep a constant size on screen
 * however far you zoom: borders (via non-scaling-stroke), army badges, and the
 * tap targets. A badge that shrinks with the map is unreadable at exactly the
 * zoom where you want to read it, and a target that shrinks is unhittable.
 *
 * Interaction is tap-source then tap-target. There is deliberately no
 * drag-to-attack: drag is how you pan, and the two gestures fight each other
 * on a touchscreen.
 */

const MIN_SCALE = 1;
const MAX_SCALE = 7;
/** Movement beyond this many px means the gesture was a pan, not a tap. */
const TAP_SLOP = 8;
/** Below this zoom, names are unreadable, so only army counts are drawn. */
const LABEL_SCALE = 2.2;

const VIEW_BOX = classic42.viewBox as [number, number, number, number];
const MAP_W = VIEW_BOX[2];
const MAP_H = VIEW_BOX[3];

/** Hatches, so seats stay distinguishable without relying on hue alone. */
const PATTERN_COUNT = 6;

interface Transform {
  readonly k: number;
  readonly x: number;
  readonly y: number;
}

export function MapView({
  game,
  seatId,
  selected,
  targets,
  onSelect,
}: {
  game: GameState;
  seatId: SeatId | null;
  selected?: TerritoryId | null | undefined;
  targets?: ReadonlySet<TerritoryId> | undefined;
  onSelect?: ((id: TerritoryId) => void) | undefined;
}): ReactNode {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [view, setView] = useState<Transform>({ k: 1, x: 0, y: 0 });

  // Live pointer bookkeeping lives in a ref: a moving finger must not
  // re-render the whole map on every event.
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const gesture = useRef({ moved: 0, startDist: 0, startK: 1, lastX: 0, lastY: 0 });

  const clamp = useCallback((next: Transform): Transform => {
    const k = Math.max(MIN_SCALE, Math.min(MAX_SCALE, next.k));
    // Keep the map overlapping its frame. Panning it off into blank space is
    // disorienting, and there is nothing out there to look at.
    const maxX = MAP_W * (k - 1);
    const maxY = MAP_H * (k - 1);
    return {
      k,
      x: Math.max(-maxX, Math.min(0, next.x)),
      y: Math.max(-maxY, Math.min(0, next.y)),
    };
  }, []);

  /** Zoom about a point in map units, so a pinch stays anchored under the fingers. */
  const zoomAbout = useCallback(
    (target: number, px: number, py: number) => {
      setView((v) => {
        const k = Math.max(MIN_SCALE, Math.min(MAX_SCALE, target));
        const ratio = k / v.k;
        return clamp({ k, x: px - (px - v.x) * ratio, y: py - (py - v.y) * ratio });
      });
    },
    [clamp],
  );

  const toMapUnits = useCallback((clientX: number, clientY: number) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return { x: 0, y: 0 };
    return {
      x: ((clientX - rect.left) / rect.width) * MAP_W,
      y: ((clientY - rect.top) / rect.height) * MAP_H,
    };
  }, []);

  const onPointerDown = (e: ReactPointerEvent<SVGSVGElement>): void => {
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    gesture.current.moved = 0;
    gesture.current.lastX = e.clientX;
    gesture.current.lastY = e.clientY;

    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      gesture.current.startDist = Math.hypot(a!.x - b!.x, a!.y - b!.y);
      gesture.current.startK = view.k;
    }
  };

  const onPointerMove = (e: ReactPointerEvent<SVGSVGElement>): void => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointers.current.size >= 2) {
      const [a, b] = [...pointers.current.values()];
      const dist = Math.hypot(a!.x - b!.x, a!.y - b!.y);
      if (gesture.current.startDist > 0) {
        gesture.current.moved = TAP_SLOP + 1; // a pinch is never a tap
        const mid = toMapUnits((a!.x + b!.x) / 2, (a!.y + b!.y) / 2);
        zoomAbout((dist / gesture.current.startDist) * gesture.current.startK, mid.x, mid.y);
      }
      return;
    }

    const dx = e.clientX - gesture.current.lastX;
    const dy = e.clientY - gesture.current.lastY;
    gesture.current.moved += Math.abs(dx) + Math.abs(dy);
    gesture.current.lastX = e.clientX;
    gesture.current.lastY = e.clientY;

    if (gesture.current.moved > TAP_SLOP) {
      const rect = svgRef.current?.getBoundingClientRect();
      const scale = rect && rect.width > 0 ? MAP_W / rect.width : 1;
      setView((v) => clamp({ k: v.k, x: v.x + dx * scale, y: v.y + dy * scale }));
    }
  };

  const endPointer = (e: ReactPointerEvent<SVGSVGElement>): void => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) gesture.current.startDist = 0;
  };

  // Wheel zoom for the desktop case. Attached by hand because a passive
  // listener cannot preventDefault, and React attaches wheel passively.
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return undefined;
    const onWheel = (e: WheelEvent): void => {
      e.preventDefault();
      const at = toMapUnits(e.clientX, e.clientY);
      setView((v) => {
        const k = Math.max(MIN_SCALE, Math.min(MAX_SCALE, v.k * (e.deltaY < 0 ? 1.15 : 1 / 1.15)));
        const ratio = k / v.k;
        return clamp({ k, x: at.x - (at.x - v.x) * ratio, y: at.y - (at.y - v.y) * ratio });
      });
    };
    svg.addEventListener('wheel', onWheel, { passive: false });
    return () => svg.removeEventListener('wheel', onWheel);
  }, [clamp, toMapUnits]);

  const tap = (id: TerritoryId) => (): void => {
    if (gesture.current.moved > TAP_SLOP) return; // that gesture was a pan
    onSelect?.(id);
  };

  const seatIndex = (owner: SeatId | null): number =>
    owner === null ? -1 : game.seats.findIndex((s) => s.id === owner);
  const colorOf = (owner: SeatId | null): string =>
    game.seats.find((s) => s.id === owner)?.color ?? '#8b93a1';

  const byId = new Map(classic42.territories.map((t) => [t.id, t]));
  const showLabels = view.k >= LABEL_SCALE;

  return (
    <div className="mapwrap">
      <svg
        ref={svgRef}
        className="map"
        viewBox={`0 0 ${MAP_W} ${MAP_H}`}
        role="group"
        aria-label="World map"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endPointer}
        onPointerCancel={endPointer}
      >
        <defs>
          <pattern id="pat-1" width="8" height="8" patternUnits="userSpaceOnUse">
            <path d="M0 8 L8 0" stroke="#000" strokeWidth="1.4" opacity=".28" />
          </pattern>
          <pattern id="pat-2" width="8" height="8" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="1.3" fill="#000" opacity=".26" />
          </pattern>
          <pattern id="pat-3" width="8" height="8" patternUnits="userSpaceOnUse">
            <path d="M0 4 H8 M4 0 V8" stroke="#000" strokeWidth="1.1" opacity=".22" />
          </pattern>
          <pattern id="pat-4" width="8" height="8" patternUnits="userSpaceOnUse">
            <path d="M4 0 V8" stroke="#000" strokeWidth="1.5" opacity=".24" />
          </pattern>
          <pattern id="pat-5" width="8" height="8" patternUnits="userSpaceOnUse">
            <path d="M0 0 L8 8" stroke="#000" strokeWidth="1.4" opacity=".28" />
          </pattern>
        </defs>

        <g transform={`translate(${view.x} ${view.y}) scale(${view.k})`}>
          {/* Borders the rules declare but a flat map cannot show as touching. */}
          <g className="links">
            {(classic42.connections ?? []).map((link) => {
              const a = byId.get(link.a);
              const b = byId.get(link.b);
              if (!a?.label || !b?.label) return null;
              return (
                <line
                  key={`${link.a}-${link.b}`}
                  x1={a.label.x} y1={a.label.y + 7}
                  x2={b.label.x} y2={b.label.y + 7}
                  vectorEffect="non-scaling-stroke"
                />
              );
            })}
          </g>

          {classic42.territories.map((t) => {
            const state = game.territories[t.id];
            const owner = state?.owner ?? null;
            const mine = owner !== null && owner === seatId;
            const idx = seatIndex(owner);
            const classes = [
              'terrshape',
              mine ? 'mine' : '',
              selected === t.id ? 'selected' : '',
              targets?.has(t.id) ? 'target' : '',
            ].filter(Boolean).join(' ');

            return (
              <g key={t.id} className={classes}>
                <path d={t.path ?? ''} fill={colorOf(owner)} vectorEffect="non-scaling-stroke" />
                {idx > 0 && (
                  <path
                    d={t.path ?? ''}
                    fill={`url(#pat-${idx % PATTERN_COUNT})`}
                    pointerEvents="none"
                  />
                )}
                <path d={t.path ?? ''} fill="transparent" onClick={tap(t.id)} />
                {t.hit && (
                  <circle
                    className="hit"
                    cx={t.hit.cx}
                    cy={t.hit.cy}
                    // Counter-scaled, so a small territory stays thumb-sized
                    // when the whole map is in view.
                    r={t.hit.r / view.k}
                    onClick={tap(t.id)}
                  />
                )}
              </g>
            );
          })}

          {classic42.territories.map((t) => {
            const state = game.territories[t.id];
            if (!state || !t.badge) return null;
            return (
              <g
                key={`b-${t.id}`}
                className="badge"
                transform={`translate(${t.badge.x} ${t.badge.y}) scale(${1 / view.k})`}
                pointerEvents="none"
              >
                {showLabels && <text className="tname" y={-18}>{t.short ?? t.name}</text>}
                <circle r={12} />
                <text className="tcount" y={4}>{state.armies}</text>
              </g>
            );
          })}
        </g>
      </svg>

      <div className="mapzoom">
        <button className="small" aria-label="Zoom in" onClick={() => zoomAbout(view.k * 1.4, MAP_W / 2, MAP_H / 2)}>+</button>
        <button className="small" aria-label="Zoom out" onClick={() => zoomAbout(view.k / 1.4, MAP_W / 2, MAP_H / 2)}>−</button>
        <button className="small" aria-label="Fit the whole map" onClick={() => setView({ k: 1, x: 0, y: 0 })}>⤢</button>
      </div>
    </div>
  );
}
